import { createPortal } from "react-dom";
import React, { useRef } from "react";
import { DraftChange } from "./productDraftDiff";
import { useSheetFocus } from "./useSheetFocus";

type Props = {
  open: boolean;
  changes: DraftChange[];
  /** True when there are edits on screen that aren't in the saved draft yet; they are saved first. */
  unsavedOnScreen: boolean;
  busy: boolean;
  onKeep: () => void;
  onPublish: () => void;
  /** Photo views still missing ("blouse photo", "video"). Shown as a note; never blocks. */
  goodToHave?: string[];
  onAddViews?: () => void;
};

/** "Publish N changes?" — shows what goes live (old → new) before the buyer-facing version changes. */
const PublishChangesSheet = ({ open, changes, unsavedOnScreen, busy, onKeep, onPublish, goodToHave, onAddViews }: Props) => {
  const sheetRef = useRef<HTMLDivElement>(null);
  useSheetFocus(open, sheetRef, onKeep);
  if (!open) return null;
  const count = changes.length;
  // Portal to <body>: a transformed page wrapper would otherwise trap position: fixed.
  return createPortal(
    <div className="ps-shell ps-sheet-scrim" role="presentation" onClick={onKeep}>
      <div
        ref={sheetRef}
        className="ps-sheet"
        role="dialog"
        tabIndex={-1}
        aria-modal="true"
        aria-labelledby="ps-publish-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="ps-sheet__handle" />
        <h2 id="ps-publish-title">
          {count ? `Publish ${count} change${count === 1 ? "" : "s"}?` : "Publish changes?"}
        </h2>
        {count ? (
          <ul className="ps-diff">
            {changes.map((c) => (
              <li key={c.key}>
                <strong>{c.label}</strong>
                {c.from ? (
                  <>
                    <s>{c.from}</s> → <b>{c.to}</b>
                  </>
                ) : (
                  <b>{c.to}</b>
                )}
              </li>
            ))}
          </ul>
        ) : null}
        {unsavedOnScreen || !count ? (
          <p className="ps-note" style={{ margin: "13px 0" }}>
            Your latest edits on this screen are saved first, then published.
          </p>
        ) : null}
        {goodToHave && goodToHave.length ? (
          <div className="ps-goodtohave" role="note">
            <span className="ps-goodtohave__dash" aria-hidden="true">—</span>
            <span>
              Good to have, not required: <b>{goodToHave.join(", ")}</b> {goodToHave.length === 1 ? "is" : "are"} still missing.
            </span>
            {onAddViews ? (
              <button type="button" className="ps-btn ps-btn--ghost ps-btn--sm" onClick={onAddViews}>
                <span>Add</span>
              </button>
            ) : null}
          </div>
        ) : null}
        <p style={{ fontSize: 12, color: "#6e6e73", margin: "8px 0 13px" }}>
          After publishing, the list, catalogues and the buyer’s certificate show the new version.
        </p>
        <div style={{ display: "flex", gap: 8 }}>
          <button type="button" className="ps-btn ps-btn--line" onClick={onKeep} disabled={busy}>
            <span>Keep as draft</span>
          </button>
          <button type="button" className="ps-btn ps-btn--blue ps-btn--wide" onClick={onPublish} disabled={busy}>
            <span>{busy ? "Publishing…" : "Publish changes"}</span>
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default PublishChangesSheet;
